// The mileage log as CSV, with exactly the app's MileageCSVExporter columns and formats: ISO
// dates, 24-hour times, one-decimal distances, rates to three places, money to the cent with
// halves rounded up, point decimals without grouping, RFC 4180 quoting and CRLF line ends.

import type { MileageReport } from "./report.ts";
import { rateAmount, roundMoney } from "./rates.ts";
import { unitSymbol } from "./format.ts";

export const csvHeader = [
  "Date", "Start Time", "End Time", "From", "To", "Purpose", "Category", "Vehicle", "Distance",
  "Distance Unit", "Rate", "Value", "Currency", "Round Trip", "Notes",
];

export function escapeCSV(field: string): string {
  if (!/[",\r\n]/.test(field)) return field;
  return `"${field.replaceAll('"', '""')}"`;
}

function pad(value: number): string {
  return value.toString().padStart(2, "0");
}

/** "2026-09-01" in local time. */
export function csvDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** "17:30" in local time. */
export function csvTime(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "0.725", "0.7": up to three places, no trailing zeros. */
export function csvRate(amount: number): string {
  return (Math.round(amount * 1000) / 1000).toString();
}

/** "8.70" */
export function csvMoney(value: number): string {
  return roundMoney(value).toFixed(2);
}

export function mileageCSV(report: MileageReport): string {
  const rows = report.rows.map((row) => [
    csvDate(row.start),
    csvTime(row.start),
    row.end ? csvTime(row.end) : "",
    row.from ?? "",
    row.to ?? "",
    row.purpose ?? "",
    row.category.name,
    row.vehicleName ?? "",
    row.distance.toFixed(1),
    unitSymbol[report.unit],
    row.rate ? csvRate(rateAmount(row.rate)) : "",
    row.value !== undefined ? csvMoney(row.value) : "",
    row.rate?.currencyCode ?? "",
    row.isRoundTrip ? "Yes" : "No",
    row.notes ?? "",
  ]);
  return [csvHeader, ...rows].map((fields) => `${fields.map(escapeCSV).join(",")}\r\n`).join("");
}
